export interface LeaveTypeInterface {
  id: number,
  name: string
  translatedName: string
  maxDays: number;
  article?: string;
  description?: string;
}

export interface CreateLeaveRequestInterface {
  requestBy: string;
  firstApprover: string;
  secondApprover: string;
  leaveTypeId: number;
  dateFrom: Date;
  dateTo: Date;
  selectedDays: string;
  notes?: string;

  [key: string]: string | number | Date | undefined;
}

export interface RequestCancellationInterface {
  id: number;
  requestedOn: string;
  isApproved: boolean;
  reviewedBy: string;
  reviewedOn: Date;
}

export interface LeaveRequestInterface {
  id: number;
  requestBy: string;
  requestOn: string;
  leaveType: LeaveTypeInterface;
  dateFrom: string;
  dateTo: string;
  days: number;
  notes: string;
  isApproved: boolean;
  reviewedBy: string;
  firstApprover: string;
  secondApprover: string;
  cancellation?: RequestCancellationInterface;
  file?: string;
  selectedDays: string;
}

export interface ApproversInterface {
  departmentId: string;
  firstApprover: string;
  secondApprover: string;
}

export enum LeaveCalendarScope {
  LEAVE_REQUEST_REVIEW = 'calendar_request_review',
}

export interface UsedLeaveTypesCountsInterface {
  type: string;
  count: number;
  maxDays: number;
  description?: string;
  article: string;
}

export interface EmployeeLeavesInterface {
  userId: string,
  paidLeaves: number,
  additionalPaidLeaves: number
  isManager: boolean;
  canUsePaidLeaveAfter?: string;
  specialAdditionalPaidLeaves: number;
  prevBalance: number;
  nextYearLeaves: number;
}

export interface EditEmployeeLeavesInterface {
  paidLeaves?: number;
  additionalPaidLeaves?: number;
  isManager?: boolean;
  canUsePaidLeaveAfter?: string | null;
  prevBalance?: number;
}

export interface EmployeeYearPanelInterface {
  prevBalance: number;
  annualAllowance: number;
  takenThisYear: number;
  bookedThisYear: number;
  currentBalance: number;
  additionalPaidLeave: number;
}

export interface LeaveRequestUpdateInterface {
  requestBy?: string;
  leaveTypeId?: number;
  dateFrom?: Date;
  dateTo?: Date;
  days?: number;
  selectedDays?: string;
  requestOn?: Date;
}

export interface LeavesRepositoryInterface {
  createRequest(data: CreateLeaveRequestInterface, document?: File): Promise<LeaveRequestInterface>;

  getUserApprovedLeaves(userId: string): Promise<LeaveRequestInterface[]>;

  getAllUserLeaves(userId: string, page: number): Promise<[LeaveRequestInterface[], number]>;

  getUserUpcomingLeaves(userId: string): Promise<LeaveRequestInterface[]>;

  getLeaveTypes(): Promise<LeaveTypeInterface[]>;

  getAllLeaves(date?: string): Promise<LeaveRequestInterface[]>;

  getPendingRequests(): Promise<LeaveRequestInterface[]>;

  reviewRequest(requestId: number, isApproved: boolean): Promise<void>;

  getAllApprovers(): Promise<ApproversInterface[]>;

  getApproversByDepartment(departmentId: string): Promise<ApproversInterface>;

  saveApprovers(data: ApproversInterface): Promise<void>;

  createCancellationRequest(requestId: number): Promise<void>;

  reviewCancellationRequest(id: number, isApproved: boolean): Promise<void>;

  getByUsersIds(usersIds: string[], date?: string): Promise<LeaveRequestInterface[]>;

  getArchive(page: number, usersIds?: string[]): Promise<[LeaveRequestInterface[], number]>;

  getUsedLeaveTypesCounts(userId: string): Promise<UsedLeaveTypesCountsInterface[]>;

  getEmployeeLeaves(userId: string): Promise<EmployeeLeavesInterface>;

  getEmployeeLeavesByIds(userIds: string[]): Promise<EmployeeLeavesInterface[]>;

  editEmployeeLeaves(userId: string, data: EditEmployeeLeavesInterface): Promise<void>;

  getEmployeeYearPanel(userId: string, year: number): Promise<EmployeeYearPanelInterface>;

  updateLeaveRequest(requestId: number, updateDto: LeaveRequestUpdateInterface): Promise<LeaveRequestInterface>;
}
